import React from 'react'
import { 
    dateFormatter, 
    dateFormatter2, 
    detailDateFormatter, 
    chartDateFormatter4,
    chartDateFormatter5,
    chartDateFormatter6,
    chartDateFormatter7,
    chartDateFormatter8,
    chartDateFormatter9,
    chartDateFormatter10,
    chartDateFormatter11,
    chartDateFormatter12,
    chartDateFormatter13,
    chartDateFormatter14,
} from 'utils/common';
import {
    INTERVAL_DAY,
    INTERVAL_MINUTE,
    INTERVAL_HOUR,
    INTERVAL_WEEK,
    INTERVAL_MONTH,
    INTERVAL_YEAR
} from '../chartConfigurations';
import {
    isOptionRelativeTime,
    getRelativeTimeDates
} from '../editor/inputs/DatePicker'
import numeral from 'numeral';
import { cloneDeep, isEmpty, has, sortBy } from 'lodash';
import {
    chartData,
    chartDataPreview
} from 'api/dashboard';

export const DEFAULT_CARTESIAN_CHART_CONFIG = {
    title: '',
    legendPosition: 'below',
    chartLayout: 'stacked',
    format: 'Default',
    decimal: 0,
    yPrefix: '',
    ySuffix: '',
    yTitle: {
        enabled: false,
        value: ''
    },
    threshold: {
        enabled: false,
        name: '',
        type: '',
        value: 0,
        color: ''
    },
    yDataColors: [],
    xTitle: {
        enabled: false,
        value: ''
    },
    xGroupByColors: [],
    dateFormatter: 'dateFormatter',
    intervalQuantity: 0,
    yDataOverrides: {},
    yDataColorObj: {}
}

const DECIMAL_FORTMAT_MAP = {
    0: '0',
    1: '0.0',
    2: '0.00',
    3: '0.000',
    4: '0.0000',
    5: '0.00000',
    6: '0.000000',
    7: '0.0000000',
    8: '0.00000000'
}

export const formatDate = (date, formatterKey) => {
    const formatters = {
        "dateFormatter": (time) => dateFormatter(time, true),
        "dateFormatter2": (time) => dateFormatter2(time, false),
        "dateFormatter3": (time) => detailDateFormatter(time, true),
        'chartDateFormatter4': (time) => chartDateFormatter4(time),
        'chartDateFormatter5': (time) => chartDateFormatter5(time),
        'chartDateFormatter6': (time) => chartDateFormatter6(time),
        'chartDateFormatter7': (time) => chartDateFormatter7(time),
        'chartDateFormatter8': (time) => chartDateFormatter8(time),
        'chartDateFormatter9': (time) => chartDateFormatter9(time),
        'chartDateFormatter10': (time) => chartDateFormatter10(time),
        'chartDateFormatter11': (time) => chartDateFormatter11(time),
        'chartDateFormatter12': (time) => chartDateFormatter12(time),
        'chartDateFormatter13': (time) => chartDateFormatter13(time),
        'chartDateFormatter14': (time) => chartDateFormatter14(time),
    }
    const selectedFormatter = formatters[formatterKey] || formatters["dateFormatter"];

    const formatted = selectedFormatter(date);

    if (!formatted || formatted === 'Invalid date') {
        return '';
    }

    return formatted;
}

export const isValidTimeRange = (chartInterval, startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const timeRangeInDays = (end - start) / (1000 * 60 * 60 * 24);

    const intervalMaxAllowedDays = {
        [INTERVAL_MINUTE]: 1,
        [INTERVAL_HOUR]: 3,
        [INTERVAL_DAY]: Infinity,
        [INTERVAL_WEEK]: Infinity,
        [INTERVAL_MONTH]: Infinity,
        [INTERVAL_YEAR]: Infinity
    }

    return timeRangeInDays <= (intervalMaxAllowedDays[chartInterval] || 0);
}

export const isChartDataTimeSeries = (groupBy) => {
    return groupBy.length === 0 || groupBy.includes('time')
}

export const getCartesianChartConfig = (chartConfig) => {
    const configuration = cloneDeep(DEFAULT_CARTESIAN_CHART_CONFIG)

    const basicFields = chartConfig.basicFields
    const yAxisFields = chartConfig.dataFields
    const xAxisFields = chartConfig.xAxisFields
    const scopeFields = chartConfig.scopeFields

    // SET CONFIGURATION FOR BASIC INFO
    const chartTitle = basicFields.find(item => item.id === 'title');
    if (chartTitle) configuration.title = chartTitle.value

    const legend = basicFields.find(item => item.id === 'legend');
    if (legend && legend.selectedOption) configuration.legendPosition = legend.selectedOption.value

    // SET CONFIGURATION FOR Y-AXIS INFO
    const enableYTitle = yAxisFields.find(item => item.id === 'enableYAxisTitle');
    if (enableYTitle && enableYTitle.value) {
        configuration.yTitle.enabled = enableYTitle.value

        const yTitle = yAxisFields.find(item => item.id === 'yAxisTitle');
        if (yTitle) configuration.yTitle.value = yTitle.value
    }

    const chartLayout = yAxisFields.find(item => item.id === 'chartLayout')
    if (chartLayout && chartLayout.selectedOption) configuration.chartLayout = chartLayout.selectedOption.value

    const format = yAxisFields.find(item => item.id === 'format')
    if (format && format.selectedOption) configuration.format = format.selectedOption.value

    const decimal = yAxisFields.find(item => item.id === 'decimal')
    if (decimal && decimal.selectedOption) configuration.decimal = decimal.selectedOption.value

    const yPrefix = yAxisFields.find(item => item.id === 'prefix');
    if (yPrefix) configuration.yPrefix = yPrefix.value

    const ySuffix = yAxisFields.find(item => item.id === 'suffix');
    if (ySuffix) configuration.ySuffix = ySuffix.value

    const enableThreshold = yAxisFields.find(item => item.id === 'enableThreshold');
    if (enableThreshold && enableThreshold.value) {
        configuration.threshold.enabled = enableThreshold.value

        if (configuration.threshold.enabled) {
            const thresholdName = yAxisFields.find(item => item.id === "thresholdName");
            if (thresholdName) configuration.threshold.name = thresholdName.value

            const thresholdType = yAxisFields.find(item => item.id === "thresholdType");
            if (thresholdType && thresholdType.selectedOption) {
                configuration.threshold.type = thresholdType.selectedOption.value

                if (thresholdType.enableCustomConfigs) {
                    const thresholdConfig = thresholdType.customConfigs.find(item => item.id === configuration.threshold.type);
                    if (thresholdConfig) {
                        configuration.threshold.value = thresholdConfig.value
                        configuration.threshold.color = thresholdConfig.color
                    }
                }
            }
        }

    }

    const yData = yAxisFields.find(item => item.id === 'data');
    if (yData && yData.enableCustomConfigs) {
        const yDataColors = yData.customConfigs.map(item => item.color) || []
        configuration.yDataColors = yDataColors

        const yDataOverrides = {}
        const yDataColorObj = {}
        yData.customConfigs.forEach(item => {
            yDataOverrides[item.id] = item.value
            yDataColorObj[item.id] = item.color
        })
        configuration.yDataOverrides = yDataOverrides
        configuration.yDataColorObj = yDataColorObj
    }

    // SET CONFIGURATION FOR X-AXIS INFO
    const enableXTitle = xAxisFields.find(item => item.id === 'enableXAxisTitle');
    if (enableXTitle && enableXTitle.value) {
        configuration.xTitle.enabled = enableXTitle.value

        const xTitle = xAxisFields.find(item => item.id === 'xAxisTitle');
        if (xTitle) configuration.xTitle.value = xTitle.value
    }

    const dateFormatter = xAxisFields.find(item => item.id === 'timeFormat');
    if (dateFormatter && dateFormatter.selectedOption) configuration.dateFormatter = dateFormatter.selectedOption.value

    const xGroupBy = xAxisFields.find(item => item.id === 'groupBy');
    if (xGroupBy && xGroupBy.enableCustomConfigs) {
        const xGroupByColors = xGroupBy.customConfigs.map(item => item.color) || []
        configuration.xGroupByColors = xGroupByColors
    }


    // SET CONFIGURATION FOR SCOPE INFO
    const intervalQuantity = scopeFields.find(item => item.id === 'intervalQuantity');
    if (intervalQuantity) configuration.intervalQuantity = intervalQuantity.value

    return configuration;
}

export const DEFAULT_TABLE_CHART_CONFIG = {
    title: '',
    metricConfigs: {},
    defaultMetricConfig: {
        metric: '',
        columnTitle: '',
        decimal: 0,
        prefix: '',
        suffix: '',
        coloring: 'background',
        thresholdType: 'percent',
        colorTiers: {
            total: 0,
            tiers: {
                0: {
                    active: false,
                    value: 0,
                    color: ''
                },
                1: {
                    active: false,
                    value: 0,
                    color: ''
                },
                2: {
                    active: false,
                    value: 0,
                    color: ''
                }
            }
        }
    },
    isRawData: false,
    interval: {
        enabled: false,
        format: 'dateFormatter2',
        title: ''
    },
}

export const getTableChartConfig = (chartConfig) => {
    const configuration = cloneDeep(DEFAULT_TABLE_CHART_CONFIG)

    const basicFields = chartConfig.basicFields
    const dataFields = chartConfig.dataFields
    const scopeFields = chartConfig.scopeFields

    // SET CONFIGURATION FOR BASIC FIELDS
    const chartTitle = basicFields.find(item => item.id === 'title');
    if (chartTitle) configuration.title = chartTitle.value

    // SET CONFIGURATION FOR METRICS CONFIG FIELDS
    const dataConfigs = dataFields.find(item => item.id === 'dataCallapsibleConfigs')
    if (dataConfigs && dataConfigs.dataConfigs && dataConfigs.dataConfigs.length > 0) {
        dataConfigs.dataConfigs.forEach(data => {
            const updatedMetricConfig = cloneDeep(configuration.defaultMetricConfig)
            const metricFields = data.fields

            const columnTitle = metricFields.find(item => item.id === 'title');
            if (columnTitle) updatedMetricConfig.columnTitle = columnTitle.value

            const decimal = metricFields.find(item => item.id === 'decimal')
            if (decimal && decimal.selectedOption) updatedMetricConfig.decimal = decimal.selectedOption.value

            const prefix = metricFields.find(item => item.id === 'prefix')
            if (prefix) updatedMetricConfig.prefix = prefix.value

            const suffix = metricFields.find(item => item.id === 'suffix')
            if (suffix) updatedMetricConfig.suffix = suffix.value

            const coloring = metricFields.find(item => item.id === 'coloring')
            if (coloring) updatedMetricConfig.coloring = coloring.selectedOption.value

            const thresholdType = metricFields.find(item => item.id === 'thresholdType')
            if (thresholdType && thresholdType.selectedOption) updatedMetricConfig.thresholdType = thresholdType.selectedOption.value

            const colorTiers = metricFields.find(item => item.id === 'colorTiers')
            if (colorTiers && colorTiers.selectedOption) updatedMetricConfig.colorTiers.total = colorTiers.selectedOption.value
            if (colorTiers.enableCustomConfigs && colorTiers.customConfigs.length > 0) {
                colorTiers.customConfigs.forEach(colorTier => {
                    if (updatedMetricConfig.colorTiers.tiers[colorTier.id]) {
                        updatedMetricConfig.colorTiers.tiers[colorTier.id].active = true
                        updatedMetricConfig.colorTiers.tiers[colorTier.id].value = colorTier.value
                        updatedMetricConfig.colorTiers.tiers[colorTier.id].color = colorTier.color
                    }
                })
            }
            configuration.metricConfigs[data.metric] = updatedMetricConfig
            configuration.metricConfigs[data.metric].metric = data.metric
        })
    }

    // SET CONFIGURATION FOR SCOPE INFO
    const columnMetricDataField = scopeFields.find(item => item.id === 'columnMetricData');
    if (columnMetricDataField && columnMetricDataField.selectedOption) {
        configuration.isRawData = columnMetricDataField.selectedOption.value === 'raw'
        if (!configuration.isRawData) {
            configuration.interval.enabled = true

            const intervalFormat = scopeFields.find(item => item.id === 'intervalFormat');
            if (intervalFormat && intervalFormat.selectedOption) configuration.interval.format = intervalFormat.selectedOption.value

            const intervalTitle = scopeFields.find(item => item.id === 'intervalTitle');
            if (intervalTitle) configuration.interval.title = intervalTitle.value.trim()
        }
    }

    return configuration;
}

export const groupByKeys = (data, keys) => {
    if (keys.length === 0) return data;

    const [key, ...remainingKeys] = keys;
    const grouped = {};

    data.forEach(item => {
        const groupValue = item.group[key];

        if (!grouped[groupValue]) {
            grouped[groupValue] = [];
        }
        grouped[groupValue].push(item);
    });

    Object.keys(grouped).forEach(groupValue => {
        grouped[groupValue] = groupByKeys(grouped[groupValue], remainingKeys);
    });

    return grouped;
}

export const groupCartesianData = (data, groupByKeys, currentGroupLevel = 0) => {
    if (currentGroupLevel >= groupByKeys.length) return data;

    const currentKey = groupByKeys[currentGroupLevel];
    const group = {};

    data.forEach(element => {
        const groupValue = element.group[currentKey]

        if (!group[groupValue]) {
            group[groupValue] = {
                [currentKey]: groupValue,
                groups: []
            }
        }

        if (currentGroupLevel === groupByKeys.length - 1) {
            group[groupValue].groups.push({
                values: element.values
            })
        } else {
            group[groupValue].groups.push(element)
        }
    });

    if (currentGroupLevel < groupByKeys.length - 1) {
        Object.keys(group).forEach(key => {
            group[key].groups = this.groupData(group[key].groups, groupByKeys, currentGroupLevel + 1)
        })
    }

    return Object.values(group)
}

export const customFormatDistance = (distance) => {
    return distance.replace('about', 'Last');
}

export const generateRandomValue = (arrayLength) => {
    if (arrayLength === 1) {
        return [100]; // Special case for single element arrays
    }

    if (arrayLength === 2) {
        return [0, 50]
    }

    if (arrayLength === 3) {
        return [0, 25, 75]
    }

    if (arrayLength === 4) {
        return [0, 20, 40, 60]
    }
}

export const getRandomHex = (list) => {
    if (list.length < 7) {
        return colorList[list.length]
    }
    return '#' + Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0');
}

export const getCustomColorLabel = {
    0: 'Min',
    1: 'Threshold 1',
    2: 'Threshold 2',
    3: 'Threshold 3',
}

export const formatYAxisLabel = (value, format, decimal) => {
    if (value === null || value === undefined) return ''; // Handle null or undefined values
    const decimalFormat = DECIMAL_FORTMAT_MAP[decimal] || ''
    switch (format) {
        case '10':
            return numeral(value / 10).format(decimalFormat) + ' (×10)'; // Tens
        case '100':
            return numeral(value / 100).format(decimalFormat) + ' (×100)'; // Hundreds
        case '1000':
            return numeral(value / 1000).format(decimalFormat) + 'k'; // Thousands
        case '1000000':
            return numeral(value / 1000000).format(decimalFormat) + 'Mil'; // Millions
        case '1000000000':
            return numeral(value / 1000000000).format(decimalFormat) + 'Bil'; // Billions
        default:
            return numeral(value).format(`${decimalFormat}a`); // Default formatting
    }
};

export const getRoundedDecimalData = (value, decimal = 2) => {
    const decimalFormat = DECIMAL_FORTMAT_MAP[decimal] || ''
    const roundedValue = numeral(value).format(decimalFormat)
    return Number(roundedValue)
}

export const roundData = (data, decimal) => {
    return data.map(item => {
        const newItem = { ...item }
        newItem.values = Object.fromEntries(
            Object.entries(newItem.values).map(([key, value]) => [key, Number(getRoundedDecimalData(value, decimal))])
        )
        return newItem
    })
}

export const getThresholdCalculatedValue = (thresholdConfig, yData, chartLayout, isUniqueGroupKeys = false, groupByKeys = []) => {
    if (thresholdConfig.type === 'absolute') {
        return numeral(thresholdConfig.value).value()
    }

    if (thresholdConfig.type === 'percent') {
        let allValues
        const validKeys = groupByKeys ? Object.values(groupByKeys) : []

        if (chartLayout === 'stacked') {
            allValues = yData.map(entry =>
                Object.entries(entry.values).reduce((sum, [key, value]) => {
                    if (isUniqueGroupKeys && !isEmpty(validKeys)) {
                        return validKeys.includes(key) ? sum + value : sum
                    } else {
                        return sum + value
                    }
                }, 0)
            )
        } else {
            allValues = yData.flatMap(entry =>
                Object.entries(entry.values).map(([key, value]) => {
                    if (isUniqueGroupKeys && !isEmpty(validKeys)) {
                        return validKeys.includes(key) ? value : null
                    } else {
                        return value
                    }
                })
            ).filter(value => value != null)
        }

        const percent = numeral(thresholdConfig.value).value()
        const sortedValues = allValues.sort((a, b) => a - b)
        const percentValue = safeParseNumber((percent / 100) * (sortedValues[sortedValues.length - 1]))

        return percentValue
    }
    return 0
}

export const DEFAULT_CARD_CHART_CONFIG = {
    title: '',
    dataOverrides: {},
    cardType: 'filledCard',
    format: 'Default',
    decimal: 0,
    prefix: '',
    suffix: '',
    thresholdType: 'percent',
    colorTiers: {
        total: 0,
        tiers: {
            0: {
                active: false,
                value: 0,
                color: ''
            },
            1: {
                active: false,
                value: 0,
                color: ''
            },
            2: {
                active: false,
                value: 0,
                color: ''
            }
        }
    }
}

export const getCardChartConfig = (chartConfig) => {
    const configuration = cloneDeep(DEFAULT_CARD_CHART_CONFIG)

    const basicFields = chartConfig.basicFields
    const dataFields = chartConfig.dataFields
    const scopeFields = chartConfig.scopeFields

    const chartTitle = basicFields.find(item => item.id === 'title')
    if (chartTitle) configuration.title = chartTitle.value

    const data = dataFields.find(item => item.id === 'data')
    if (data && data.enableCustomConfigs) {
        data.customConfigs.forEach(override => {
            configuration.dataOverrides[override.id] = override
        })
    }

    const cardType = dataFields.find(item => item.id === 'cardType')
    if (cardType && cardType.selectedOption) configuration.cardType = cardType.selectedOption.value

    const format = dataFields.find(item => item.id === 'format')
    if (format && format.selectedOption) configuration.format = format.selectedOption.value

    const decimal = dataFields.find(item => item.id === 'decimal')
    if (decimal && decimal.selectedOption) configuration.decimal = decimal.selectedOption.value

    const prefix = dataFields.find(item => item.id === 'prefix')
    if (prefix) configuration.prefix = prefix.value

    const suffix = dataFields.find(item => item.id === 'suffix')
    if (suffix) configuration.suffix = suffix.value

    const thresholdType = dataFields.find(item => item.id === 'thresholdType')
    if (thresholdType && thresholdType.selectedOption) configuration.thresholdType = thresholdType.selectedOption.value

    const colorTiers = dataFields.find(item => item.id === 'colorTiers')
    if (colorTiers && colorTiers.selectedOption) configuration.colorTiers.total = colorTiers.selectedOption.value
    if (colorTiers.enableCustomConfigs && colorTiers.customConfigs.length > 0) {
        colorTiers.customConfigs.forEach(colorTier => {
            if (configuration.colorTiers.tiers[colorTier.id]) {
                configuration.colorTiers.tiers[colorTier.id].active = true
                configuration.colorTiers.tiers[colorTier.id].value = colorTier.value
                configuration.colorTiers.tiers[colorTier.id].color = colorTier.color
            }
        })
    }

    return configuration
}

export const DEFAULT_PIE_CHART_CONFIG = {
    title: '',
    legendPosition: 'below',
    dataOverrides: {},
    dataTitle: {
        enabled: false,
        value: ''
    },
    format: 'Default',
    decimal: 0,
    prefix: '',
    suffix: '',
    alwaysShowLabels: false,
    labelDisplay: 'value'
}

export const getPieChartConfig = (chartConfig) => {
    const configuration = cloneDeep(DEFAULT_PIE_CHART_CONFIG)

    const basicFields = chartConfig.basicFields
    const dataFields = chartConfig.dataFields
    const scopeFields = chartConfig.scopeFields

    const chartTitle = basicFields.find(item => item.id === 'title')
    if (chartTitle) configuration.title = chartTitle.value

    const legend = basicFields.find(item => item.id === 'legend')
    if (legend && legend.selectedOption) configuration.legendPosition = legend.selectedOption.value

    const data = dataFields.find(item => item.id === 'data')
    if (data && data.enableCustomConfigs) {
        data.customConfigs.forEach(override => {
            configuration.dataOverrides[override.id] = override
        })
    }

    const enableDataTitle = dataFields.find(item => item.id === 'enableDataTitle')
    if (enableDataTitle && enableDataTitle.value) {
        configuration.dataTitle.enabled = enableDataTitle.value

        const dataTitle = dataFields.find(item => item.id === 'dataTitle')
        if (dataTitle) configuration.dataTitle.value = dataTitle.value
    }

    const format = dataFields.find(item => item.id === 'format')
    if (format && format.selectedOption) configuration.format = format.selectedOption.value

    const decimal = dataFields.find(item => item.id === 'decimal')
    if (decimal && decimal.selectedOption) configuration.decimal = decimal.selectedOption.value

    const prefix = dataFields.find(item => item.id === 'prefix')
    if (prefix) configuration.prefix = prefix.value

    const suffix = dataFields.find(item => item.id === 'suffix')
    if (suffix) configuration.suffix = suffix.value

    const alwaysShowLabels = dataFields.find(item => item.id === 'enableLabels')
    if (alwaysShowLabels && alwaysShowLabels.value) configuration.alwaysShowLabels = alwaysShowLabels.value

    const labelDisplay = dataFields.find(item => item.id === 'labelDisplay')
    if (labelDisplay && labelDisplay.selectedOption) configuration.labelDisplay = labelDisplay.selectedOption.value

    return configuration
}

export const getActualVisoDevices = (chart, dashboardDevices) => {
    const actualVisoDevices = []

    const chartVisoDevices = chart?.configuration?.scopeFields?.find(pop => pop.id === 'devices')?.selectedOption

    if (chartVisoDevices && !isEmpty(chartVisoDevices)) {
        actualVisoDevices.push(...chartVisoDevices.map(device => device.value))
    } else if (dashboardDevices && !isEmpty(dashboardDevices)) {
        actualVisoDevices.push(...dashboardDevices)
    }

    return actualVisoDevices
}

export const getActualTimeRange = (chart, dashboardTimeRange, isPreview = false) => {
    const actualTimeRange = {
        isDashboardTimeRange: false,
        start: '',
        end: ''
    }

    const chartTimeRange = chart?.configuration?.scopeFields?.find(pop => pop.id === 'dateRange')?.selectedOption

    if ( !isPreview && (chartTimeRange && !isEmpty(chartTimeRange)) ) {
        actualTimeRange.isDashboardTimeRange = false

        if (isOptionRelativeTime(chartTimeRange)) {
            const { start, end } = getRelativeTimeDates(chartTimeRange)
            actualTimeRange.start = start
            actualTimeRange.end = end
        } else {
            actualTimeRange.start = chartTimeRange.start
            actualTimeRange.end = chartTimeRange.end
        }

    } else if (dashboardTimeRange && !isEmpty(dashboardTimeRange)) {
        actualTimeRange.isDashboardTimeRange = true

        if (isOptionRelativeTime(dashboardTimeRange)) {
            const { start, end } = getRelativeTimeDates(dashboardTimeRange)
            actualTimeRange.start = start
            actualTimeRange.end = end
        } else {
            actualTimeRange.start = dashboardTimeRange.start
            actualTimeRange.end = dashboardTimeRange.end
        }
    }

    return actualTimeRange
}

function hashStringToDelay(str, maxDelay = 1600) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = (hash << 5) - hash + str.charCodeAt(i)
        hash |= 0
    }
    return Math.abs(hash % maxDelay);
}

export const fetchChartData = async (chart, visoDevices, timeRange) => {
    if (!timeRange?.start || !timeRange?.end) throw new Error('Error: No valid time range')
    
    if (!chart || !chart.id) {
        throw new Error('Error: Invalid chart')
    }

    const delay = hashStringToDelay(chart.id, 400)
    await new Promise(res => setTimeout(res, delay))

    const callParams = {
        chartId: chart.id,
        ...(Array.isArray(visoDevices) && visoDevices.length > 0 ? { visoDevices } : {}),
        query: {
            single: [],
            range: {
                name: 'chartTimeRange',
                value: {
                    lower: timeRange.start,
                    upper: timeRange.end
                }
            },
        }
    }
    if (process.env.NODE_ENV !== 'production') {
        try {
            // Debug: verify request issuance
            console.debug('[ChartData] request', {
                chartId: chart.id,
                visoDevicesCount: Array.isArray(visoDevices) ? visoDevices.length : 0,
                start: timeRange.start,
                end: timeRange.end
            })
        } catch (_) {}
    }

    const { data } = await chartData(callParams);

    if (process.env.NODE_ENV !== 'production') {
        try {
            console.debug('[ChartData] response', {
                chartId: chart.id,
                hasErrors: !!data?.errors,
                metricsCount: data?.data?.chartData?.metricsCount,
                groupBy: data?.data?.chartData?.groupBy,
                metricColumnsLen: data?.data?.chartData?.metricColumns?.length
            })
        } catch (_) {}
    }

    if (data.errors) {
        throw new Error('No data in selected time range')
    }

    return data.data.chartData
}

export const fetchPreviewChartData = async (previewChart, previewParams, timeRange) => {
    if (!previewChart || previewChart.metricColumns?.length === 0) {
        throw new Error('Choose a data source to see a preview')
    }

    const callParams = {
        tableName: previewChart.tableName,
        metricColumns: previewChart.metricColumns,
        dataPoint: previewParams.dataPoint,
        metricAggregations: previewParams.metricAggregations,
        intervalOption: previewParams.intervalOption,
        intervalQuantity: previewParams.intervalQuantity,
        groupByColumns: previewParams.groupByColumns,
        ...(Array.isArray(previewParams?.visoDevices) && previewParams.visoDevices.length > 0 ? { visoDevices: previewParams.visoDevices } : {}),
        // query: timeRange.isDashboardTimeRange ? {
        //   single: [],
        //   range: {
        //     name: 'chartTimeRange',
        //     value: {
        //       lower: new Date(timeRange.start).toISOString(),
        //       upper: new Date(timeRange.end).toISOString()
        //     }
        //   }
        // } : null,
        startTime: timeRange.start,
        endTime: timeRange.end
    }

    const { data } = await chartDataPreview(callParams)

    if (data.errors) {
        throw new Error('No data in selected time range')
    }

    // Ensure loader is visible briefly even if the API returns instantly
    await new Promise(res => setTimeout(res, 300))
    return data.data.previewData
}

export const chartGradientDefs = (id, color, gradientOpacity = 'default') => {
    const opacities = {
        'default': [.7, .3],
        'light': [.3, 0]
    }
    const opacity = opacities[gradientOpacity] || opacities.default

    return (
        <defs>
            <linearGradient id={`${id}Fill`} x1='0' y1='0' x2='0' y2='1'>
                <stop stopColor={color} stopOpacity={opacity[0]} />
                <stop offset='1' stopColor={color} stopOpacity={opacity[1]} />
            </linearGradient>
        </defs>
    )
}

export const legendContent = (props) => {
    const { payload, dataOverrides, align } = props
    return (
        <ul className={align === 'right' ? 'chart-legend-layout-right' : 'chart-legend-layout-horizontal'}>
            {payload.map((entry, index) => {
                return (
                <li key={`item-${index}`} className='chart-legend-element'>
                    <span className='chart-legend-element__icon-square' style={{ backgroundColor: dataOverrides[entry.payload.metric]?.color || entry.color || GROUP_BY_UNIQUE_COLORS[index % GROUP_BY_UNIQUE_COLORS.length]}} />
                    {renderLegend(entry.value, entry, dataOverrides)}
                </li>
            )})}
        </ul>
    )
}

export const renderLegend = (value, entry, dataOverrides) => {
    const metric = entry?.payload?.metric || ''
    let label = metric

    if (!isEmpty(dataOverrides) && dataOverrides[metric]?.value) {
        label = dataOverrides[metric].value
    }

    return <span className='charts-no-cartesian__legend' title={label} aria-label={label}>{label}</span>
}

export const legendPayload = (data, dataOverrides) => {
    return data.map((entry, index) => {
        return ({
            value: entry.value,
            type: 'square',
            id: entry.metric,
            payload: entry,
            color: dataOverrides[entry.metric]?.color || entry.fill || GROUP_BY_UNIQUE_COLORS[index % GROUP_BY_UNIQUE_COLORS.length]
        })
    })
}

export const renderCartesianLegend = ({ uniqueGroupByValue = {}, metricColumns, cartesianConfig, uniqueGroupByOriginalKey, iconType }) => {
    let legendItems = []

    if (isEmpty(uniqueGroupByValue)) {
        metricColumns.forEach((item, index) => {
            const name = cartesianConfig.yDataOverrides[item]
                ? cartesianConfig.yDataOverrides[item]
                : item
            const color =
                (cartesianConfig.yDataColors && cartesianConfig.yDataColors[index]) ||
                (cartesianConfig.yDataColorObj && cartesianConfig.yDataColorObj[item]) ||
                GROUP_BY_UNIQUE_COLORS[index % GROUP_BY_UNIQUE_COLORS.length]
            legendItems.push({ name: name, color: color, showItem: true })
        })
    } else {
        Object.entries(uniqueGroupByValue).map(([key, value]) => {
            const showItem = has(uniqueGroupByOriginalKey, key)
            const name = uniqueGroupByOriginalKey[key] || key
            const color = value
            legendItems.push({ name, color, showItem })
        })
    }

    const sortedArray = sortBy(legendItems, 'name')

    return (
        sortedArray.map((item, index) => item.showItem &&
            <div key={index} className='chart-legend-element'>
                <span
                    className={`chart-legend-element__icon-${iconType}`}
                    style={{ backgroundColor: item.color }}
                />
                <span className='chart-legend-element__name' title={item.name} aria-label={item.name}>{item.name}</span>
            </div>
        )
    )
}

export const GROUP_BY_UNIQUE_COLORS = [
    '#2184F9',
    '#845EC2',
    '#D65DB1',
    '#FF6F91',
    '#FF9671',
    '#80C07B',
    '#FF7A7A',
    '#007FFF',
    '#FFB347',
    '#FF3E96',
    '#5A20CB',
    '#00C49F',
    '#FF9F00',
    '#D93737',
    '#1E90FF',
    '#7B61FF',
    '#FFA07A',
    '#00FA9A',
    '#FF4500',
    '#9370DB'
]

export const formatToValidGroupByKey = (key) => {
    return key.replace(/\s+/g, '_').replace(/[^\w_]/g, '')
}

// Converts any group-by value (strings, numbers, booleans, or objects) into a displayable string label.
// This ensures consistent legend labels and valid keys for chart series.
export const getDisplayLabelFromGroupValue = (value) => {
    if (value === null || value === undefined) return ''
    const t = typeof value
    if (t === 'string') return value
    if (t === 'number' || t === 'boolean') return String(value)
    if (t === 'object') {
        // Prefer common name-like fields first
        if (Object.prototype.hasOwnProperty.call(value, 'label')) return String(value.label)
        if (Object.prototype.hasOwnProperty.call(value, 'name')) return String(value.name)
        if (Object.prototype.hasOwnProperty.call(value, 'value')) return String(value.value)
        try {
            return JSON.stringify(value)
        } catch (e) {
            return String(value)
        }
    }
    return String(value)
}

export const safeParseInt = (value) => {
    const parsed = parseInt(value)
    return isNaN(parsed) ? 0 : parsed
}

export const safeParseNumber = (value) => {
    const parsed = Number(value)
    return isNaN(parsed) ? 0 : parsed
}

export const getUniqueGroupByColors = (uniqueGroupValue, availableColorIndices, groupByValues, groupByOriginalKeys) => {
    let uniqueValue = ''
    let availableIndices = [...availableColorIndices]
    const uniqueValueColors = { ...groupByValues }
    const uniqueOriginalKeys = { ...groupByOriginalKeys }
    const uniqueOriginalValue = Object.entries(uniqueGroupValue)
        .filter(([key, value]) => key !== 'time')
        .map(([key, value]) => value)[0]

    if (availableIndices.length === 0) {
        availableIndices = Array.from(Array(GROUP_BY_UNIQUE_COLORS.length).keys())
    }

    if (uniqueOriginalValue && (typeof (uniqueOriginalValue) === 'string')) {
        uniqueValue = formatToValidGroupByKey(uniqueOriginalValue)
        uniqueOriginalKeys[uniqueOriginalValue] = uniqueValue
    }

    if (!uniqueValueColors.hasOwnProperty(`${uniqueValue}`)) {
        const randomColorIndex = Math.floor(Math.random() * availableIndices.length)
        const colorIndex = availableIndices.splice(randomColorIndex, 1)[0]
        uniqueValueColors[`${uniqueValue}`] = GROUP_BY_UNIQUE_COLORS[colorIndex]
    }
}

export const convertValuesToNumbers = (obj) => {
    return Object.entries(obj).reduce((acc, [key, value]) => {
        acc[key] = Number(value)
        return acc
    }, {})
}

export const removeUnusedMetrics = (data, metricsTableEnabled) => {
    if (metricsTableEnabled) {
        return data
    }
    const removeTables = ['cpu', 'disk', 'diskio', 'kernel', 'mem', 'processes', 'swap', 'system']
    return data.filter(set => !removeTables.includes(set.tableName))
}

export const filterSelectedMetrics = (tableName, data) => {
    return data.filter(set => set.tableName === tableName)
}

export const handleDeleteNullKeysChartData = (data) => {
    return data.map(element => {
        const newElement = { ...element }
        newElement.values = Object.keys(newElement.values).forEach(key => {
            if (item.values[key] === null) {
                delete item.values[key] // = "No Data"
            }
        });
        return newElement
    })
}